/**
 * Resize an image File to fit within maxDim (longest side), preserving aspect ratio.
 * Preserves PNG transparency; JPEG is re-encoded at quality 0.9.
 * Skips resize if the image already fits and the file is under 1.5MB.
 */
export async function resizeImage(file: File, maxDim = 1024): Promise<File> {
  if (!file.type.startsWith('image/')) return file;

  const img = await loadImage(file);
  const longest = Math.max(img.width, img.height);

  if (longest <= maxDim && file.size < 1.5 * 1024 * 1024) {
    URL.revokeObjectURL(img.src);
    return file;
  }

  const scale = Math.min(1, maxDim / longest);
  const width = Math.round(img.width * scale);
  const height = Math.round(img.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.drawImage(img, 0, 0, width, height);
  URL.revokeObjectURL(img.src);

  const isPng = file.type === 'image/png';
  const outType = isPng ? 'image/png' : 'image/jpeg';
  const quality = isPng ? undefined : 0.9;

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, outType, quality));
  if (!blob) throw new Error('Failed to encode resized image');

  return new File([blob], file.name, { type: outType });
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
}
